import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { AddressProvider } from '../../domain/address-provider';
import { IAddressV2, IAddressV2ControlContext } from '@jump-tech-frontend/domain';
import { AddressFormErrorComponent } from '../../error/address-form-error.component';
import { SearchProviderFactory } from '../../providers/search-provider.factory';
import { AddressSearchPresenter } from './address-search.presenter';
import { AddressSearchResultType, AddressSearchViewModel } from './address-search.vm';

@Component({
  selector: 'jump-tech-frontend-address-search',
  templateUrl: './address-search.component.html',
  styleUrls: ['./address-search.component.scss'],
  providers: [SearchProviderFactory, AddressSearchPresenter],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, ReactiveFormsModule, AddressFormErrorComponent]
})
export class AddressSearchComponent implements OnInit {
  @Input() countryCodes: string[];
  @Input() provider: AddressProvider;
  @Input() label: string;
  @Input() placeholder?: string;
  @Input() selectedAddress?: IAddressV2 | null;
  @Input() context: IAddressV2ControlContext | string = IAddressV2ControlContext.Desktop;
  @Input() disabled?: boolean;
  @Input() required: boolean;
  @Input() errorMessage: string;
  @Output() selectedResult: EventEmitter<IAddressV2> = new EventEmitter<IAddressV2>();

  private readonly presenter = inject(AddressSearchPresenter);
  public vm$ = new BehaviorSubject<AddressSearchViewModel | null>(null);
  @ViewChildren('resultItem') resultItems!: QueryList<ElementRef>;

  ngOnInit(): void {
    this.presenter.load(
      this.vm$,
      this.countryCodes,
      this.selectedResult,
      this.getLabel(),
      this.provider,
      this.disabled,
      this.required,
      this.selectedAddress,
      this.errorMessage,
      this.placeholder
    );
  }
  getLabel(): string {
    return `${this.label}${this.required ? ' *' : ''}`;
  }

  async search(searchTermEvent: string, container?: string): Promise<void> {
    await this.presenter.search(searchTermEvent, container);
  }

  async searchOrRetrieve(id: string, type: AddressSearchResultType = 'Address'): Promise<void> {
    const controlValue = this.vm$.value?.searchTermControl?.value ?? '';
    await this.presenter.searchOrRetrieve(id, controlValue, type);
  }

  async retrieve(id: string): Promise<void> {
    const controlValue = this.vm$.value?.searchTermControl?.value ?? '';
    await this.presenter.retrieve(id, controlValue);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.presenter.navigateDown();
      this.scrollToSelectItem();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.presenter.navigateUp();
      this.scrollToSelectItem();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selectedAddress = this.presenter.getSelectedResult();
      if (selectedAddress) {
        this.searchOrRetrieve(selectedAddress.id, selectedAddress.type);
      }
    }
  }

  scrollToSelectItem(): void {
    const item = this.resultItems?.get(this.presenter.selectedIndex);
    item?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}
